import React, { useState, useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { enqueueSnackbar } from 'notistack';
import jsPDF from 'jspdf';
import ReactDOMServer from 'react-dom/server';
import NetRisePlainLogo from '../../NRIcons/NetRisePlainLogo';
import customTheme from '../../components/customTheme';
import { capitalizeFirstLetter } from '../../utils/utils';
import { useFlag } from '@openfeature/react-sdk';
import { FEATURE_FLAGS } from '../../constants/constants';

import {
  VULNS_SUMMARY_COUNT,
  KNOWN_EXPLOITS,
  LICENSES,
  VULNERABILITIES_HIGHLIGHTED_PRODUCTS,
  CRYPTOGRAPHY_COUNT,
  MISCONFIGURATION_CHECK,
  MISCONFIGURATION_BY_CATEGORY
} from '../../graphql/queries/analytics';

import { VULNERABILITIES } from '../../graphql/queries/vulnerability';
import { DEPENDENCIES } from '../../graphql/queries/dependency';
import { GET_CREDENTIALS } from '../../graphql/queries/credential';
import { HASHES } from '../../graphql/queries/hash';
import { GET_ASSET } from '../../graphql/queries/asset';
import ApexCharts from 'apexcharts';
import { offset } from 'highcharts';
import { fontSize, style } from '@mui/system';

const CustomError = ({ message }) => <span>{message}</span>;

// Helper function to create chart and convert to high-quality image
const createChartImage = (chartOptions, chartType = 'pie', width = 400, height = 300, scaleFactor = 3) => {
  return new Promise((resolve, reject) => {
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '-9999px';
    tempDiv.style.width = `${width * scaleFactor}px`;
    tempDiv.style.height = `${height * scaleFactor}px`;
    tempDiv.style.backgroundColor = 'white';
    document.body.appendChild(tempDiv);

    const chart = new ApexCharts(tempDiv, {
      ...chartOptions,
      chart: {
        ...chartOptions.chart,
        type: chartType,
        width: width * scaleFactor,
        height: height * scaleFactor,
        animations: {
          enabled: false
        },
        toolbar: {
          show: false
        },
        background: 'transparent',
        fontFamily: 'Arial, sans-serif'
      },
      plotOptions: {
        ...chartOptions.plotOptions,
        pie: {
          ...chartOptions.plotOptions?.pie,
          customScale: 1.0,
          offsetX: 0,
          offsetY: 0
        },
        donut: {
          ...chartOptions.plotOptions?.donut,
          customScale: 1.0,
          offsetX: 0,
          offsetY: 0
        },
        bar: {
          ...chartOptions.plotOptions?.bar,
          customScale: 1.0,
          offsetY: -10
        }
      },
      title: {
        ...chartOptions.title,
        style: {
          ...chartOptions.title?.style,
          fontSize: `${parseInt(chartOptions.title?.style?.fontSize || '16') * scaleFactor}px`,
          fontWeight: 'bold'
        }
      },
      legend: {
        ...chartOptions.legend,
        fontSize: `${16 * scaleFactor}px`,
        fontWeight: 'normal'
      },
      dataLabels: {
        ...chartOptions.dataLabels,
        style: {
          fontSize: `${16 * scaleFactor}px`,
          fontWeight: 'bold',
          colors: ['#fff']
        }
      },
      xaxis: {
        ...chartOptions.xaxis,
        labels: {
          ...chartOptions.xaxis?.labels,
          style: {
            fontSize: `${16 * scaleFactor}px`
          }
        }
      },
      yaxis: {
        ...chartOptions.yaxis,
        labels: {
          ...chartOptions.yaxis?.labels,
          style: {
            fontSize: `${16 * scaleFactor}px`
          }
        }
      }
    });

    chart
      .render()
      .then(() => {
        chart
          .dataURI()
          .then(({ imgURI, blob }) => {
            document.body.removeChild(tempDiv);
            chart.destroy();
            resolve(imgURI);
          })
          .catch(error => {
            document.body.removeChild(tempDiv);
            chart.destroy();
            reject(error);
          });
      })
      .catch(error => {
        document.body.removeChild(tempDiv);
        reject(error);
      });
  });
};

// Chart configuration functions
const createVulnerabilitiesChart = vulnSummary => {
  const data = [vulnSummary.low || 0, vulnSummary.medium || 0, vulnSummary.high || 0, vulnSummary.critical || 0];

  const total = data.reduce((sum, val) => sum + val, 0);
  if (total === 0) return null;

  return {
    series: data,
    chart: {
      type: 'pie'
    },
    labels: [`Low - ${vulnSummary?.low}`, `Medium - ${vulnSummary?.medium}`, `High - ${vulnSummary?.high}`, `Critical - ${vulnSummary?.critical}`],
    colors: [
      customTheme.palette.severityPalette[2].main,
      customTheme.palette.severityPalette[3].main,
      customTheme.palette.severityPalette[4].main,
      customTheme.palette.severityPalette[5].main
    ],
    title: {
      text: 'Vulnerabilities by Severity',
      offsetX: 150,
      style: {
        fontSize: '25px',
        fontWeight: 'bold'
      }
    },
    legend: {
      position: 'right',
      fontSize: '25px',
      offsetY: 550,
      itemMargin: {
        vertical: 10
      },
      markers: {
        width: 50,
        height: 50,
        radius: 25,
        offsetX: -15,
        offsetY: 10
      }
    },
    dataLabels: {
      style: {
        fontSize: '20px'
      },
      enabled: true,
      formatter: function (val) {
        return Math.round(val) + '%';
      }
    },
    plotOptions: {
      pie: {
        customScale: 1.0,
        offsetX: 0,
        offsetY: 0,
        expandOnClick: false
      }
    },
    stroke: {
      width: 2,
      colors: ['#fff']
    }
  };
};

const createKnownExploitsChart = exploits => {
  const data = [exploits.totalActors || 0, exploits.totalAttacks || 0];
  const total = data.reduce((sum, val) => sum + val, 0);
  if (total === 0) return null;

  return {
    series: [
      {
        name: 'Count',
        data: data
      }
    ],
    chart: {
      type: 'bar',
      height: 300
    },
    xaxis: {
      categories: ['Total Actors', 'Total Attacks']
    },
    colors: ['#008FFB'],
    title: {
      text: 'Known Exploits Overview',
      align: 'center',
      style: {
        fontSize: '25px',
        fontWeight: 'bold'
      }
    },
    dataLabels: {
      enabled: true,
      offsetY: -10
    },
    plotOptions: {
      bar: {
        customScale: 1.0,
        offsetY: -40,
        expandOnClick: false
      }
    }
  };
};

const createCryptographyChart = crypto => {
  const data = [crypto.certificates || 0, crypto.publicKeys || 0];
  const total = data.reduce((sum, val) => sum + val, 0);
  if (total === 0) return null;

  return {
    series: data,
    chart: {
      type: 'pie'
    },
    labels: [`Certificates - ${crypto?.certificates}`, `Public Keys - ${crypto?.publicKeys}`],
    colors: ['#00E396', '#775DD0'],
    title: {
      text: 'Cryptography Components',
      offsetX: 180,
      style: {
        fontSize: '30px',
        fontWeight: 'bold'
      }
    },
    legend: {
      position: 'right',
      fontSize: '50px',
      offsetY: 630,
      itemMargin: {
        vertical: 10
      },
      markers: {
        width: 50,
        height: 50,
        radius: 25,
        offsetX: -15,
        offsetY: 10
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return Math.round(val) + '%';
      }
    },
    plotOptions: {
      pie: {
        customScale: 1.0,
        offsetX: 0,
        offsetY: 0,
        expandOnClick: false
      }
    },
    stroke: {
      width: 2,
      colors: ['#fff']
    }
  };
};

const createMisconfigurationsChart = misconfig => {
  const data = [misconfig.passed || 0, misconfig.failed || 0, misconfig.notApplicable || 0];
  const total = data.reduce((sum, val) => sum + val, 0);
  if (total === 0) return null;

  return {
    series: data,
    chart: {
      type: 'pie'
    },
    labels: [`Passed - ${misconfig?.passed}`, `Failed - ${misconfig?.failed}`, `Not Applicable - ${misconfig?.notApplicable}`],
    colors: ['#15A696', '#E92F50', '#9E9E9E'],
    title: {
      text: 'Misconfigurations Summary',
      offsetX: 180,
      style: {
        fontSize: '30px',
        fontWeight: 'bold'
      }
    },
    legend: {
      position: 'right',
      fontSize: '25px',
      offsetY: 680,
      itemMargin: {
        vertical: 10
      },
      markers: {
        width: 50,
        height: 50,
        radius: 25,
        offsetX: -15,
        offsetY: 10
      }
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '20px'
      },
      formatter: function (val) {
        return Math.round(val) + '%';
      }
    },
    plotOptions: {
      pie: {
        customScale: 1.0,
        offsetX: 0,
        offsetY: 0,
        expandOnClick: false
      }
    },
    stroke: {
      width: 2,
      colors: ['#fff']
    }
  };
};

const createMisconfigurationsByCategoryChart = misconfigByCat => {
  if (!misconfigByCat || misconfigByCat.length === 0) return null;

  const categories = misconfigByCat.map(cat => capitalizeFirstLetter(cat.category));
  const passedData = misconfigByCat.map(cat => cat.passed || 0);
  const failedData = misconfigByCat.map(cat => cat.failed || 0);
  const notApplicableData = misconfigByCat.map(cat => cat.notApplicable || 0);

  return {
    series: [
      {
        name: 'Passed',
        data: passedData
      },
      {
        name: 'Failed',
        data: failedData
      },
      {
        name: 'Not Applicable',
        data: notApplicableData
      }
    ],
    chart: {
      type: 'bar',
      height: 400,
      stacked: true
    },
    xaxis: {
      categories: categories,
      labels: {
        rotate: -45,
        maxHeight: 230
      }
    },
    colors: ['#15A696', '#E92F50', '#9E9E9E'],
    title: {
      text: 'Misconfigurations by Category',
      align: 'center',
      style: {
        fontSize: '16px',
        fontWeight: 'bold'
      }
    },
    legend: {
      position: 'top',
      markers: {
        width: 35,
        height: 35
      }
    },
    dataLabels: {
      enabled: false
    }
  };
};

// Helper Function to Convert SVG to Data URL
const getLogoDataUrl = (color, svgComponentWidth, svgComponentHeight) => {
  const svgString = ReactDOMServer.renderToString(<NetRisePlainLogo color={color} width={svgComponentWidth} height={svgComponentHeight} />);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const logoWidthMm = 10;
  const logoHeightMm = 8.75;
  const dpi = 300;

  canvas.width = Math.round((logoWidthMm / 25.4) * dpi);
  canvas.height = Math.round((logoHeightMm / 25.4) * dpi);

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/png');
      resolve(dataUrl);
    };
    img.onerror = error => {
      console.error('Error loading SVG for canvas conversion:', error);
      reject(error);
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));
  });
};

export const useGeneratePDF = () => {
  const client = useApolloClient();
  const [isGenerating, setIsGenerating] = useState(false);
  const { value: isComponentIdentifiedViaEnabled } = useFlag(FEATURE_FLAGS.COMPONENT_IDENTIFIED_VIA, false);
  const { value: isComponentConfidenceEnabled } = useFlag(FEATURE_FLAGS.COMPONENT_CONFIDENCE, false);
  const { value: isAiRelatedDependenciesEnabled } = useFlag(FEATURE_FLAGS.AI_RELATED_DEPENDENCIES, false);

  const handleGeneratePDF = useCallback(
    async (componentId, onComplete) => {
      const snackBarErrorMessage = 'Failed to generate report.';

      if (!componentId) return;

      setIsGenerating(true);
      const assetArgs = { args: { assetId: componentId } };
      const analyticsArgs = { args: { composedAssetId: componentId } };
      const vulnerabilityArgs = {
        variables: {
          args: {
            assetId: componentId,
            cursor: { first: 5 },
            sort: { field: 'EPSSPERCENTILE', order: 'DESC' },
            filter: {
              fields: [
                {
                  fieldName: 'VULNERABILITYREMEDIATIONSTATUS',
                  value: 'STATUS_AFFECTED&STATUS_UNDER_INVESTIGATION&STATUS_UNSPECIFIED',
                  operation: 'ENUM'
                }
              ]
            }
          }
        }
      };
      const vulnerabilityThreatActorsArgs = {
        variables: {
          args: {
            assetId: componentId,
            cursor: { first: 5 },
            sort: { field: 'SEVERITY', order: 'DESC' },
            filter: {
              inThreatActors: { eq: true },
              fields: [
                {
                  fieldName: 'MATURITY',
                  value: 'weaponized',
                  operation: 'CONTAINS'
                },
                {
                  fieldName: 'VULNERABILITYREMEDIATIONSTATUS',
                  value: 'STATUS_AFFECTED&STATUS_UNDER_INVESTIGATION&STATUS_UNSPECIFIED',
                  operation: 'ENUM'
                }
              ]
            }
          }
        }
      };
      const dependenciesTopFiveVulnCountArgs = {
        variables: {
          args: {
            composedAssetId: componentId,
            cursor: { first: 5 },
            sort: { field: 'VULNERABILITY', order: 'DESC' },
            showOriginalState: false
          },
          includeIdentifiedVia: isComponentIdentifiedViaEnabled,
          includeConfidence: isComponentConfidenceEnabled,
          includeAiRelatedDependencies: isAiRelatedDependenciesEnabled
        }
      };
      const crackedCredentialsUserArgs = {
        variables: {
          args: {
            assetId: componentId,
            filter: { fields: [{ fieldName: 'CRACKED', value: 'True', operation: 'EQUAL' }] },
            cursor: { first: 50 }
          }
        }
      };
      const crackedCredentialsUserHash = {
        variables: {
          args: {
            assetId: componentId,
            filter: { fields: [{ fieldName: 'CRACKED', value: 'True', operation: 'EQUAL' }] },
            cursor: { first: 50 }
          }
        }
      };

      try {
        const logoDataUrl = await getLogoDataUrl('#FF0101', '40', '35');

        const safeQuery = (promise, fallback) => promise.catch(() => fallback);

        const [
          assetRes,
          vulnRes,
          exploitsRes,
          licensesRes,
          highlightedProductsRes,
          cryptoRes,
          misconfigRes,
          misconfigCatRes,
          vulnerabilitiesByEPSS,
          vulnsThreatActorsWeaponized,
          dependenciesTopFiveVulnCount,
          crackCredentialsUser,
          crackedCredentialsHash
        ] = await Promise.all([
          safeQuery(
            client.query({
              query: GET_ASSET,
              fetchPolicy: 'no-cache',
              variables: { ...assetArgs }
            }),
            { data: { asset: null } }
          ),
          safeQuery(client.query({ query: VULNS_SUMMARY_COUNT, fetchPolicy: 'no-cache', variables: analyticsArgs }), {
            data: { analytics: { vulnerabilitiesSummaryCount: {} } }
          }),
          safeQuery(client.query({ query: KNOWN_EXPLOITS, fetchPolicy: 'no-cache', variables: analyticsArgs }), {
            data: { analytics: { knownExploits: {} } }
          }),
          safeQuery(client.query({ query: LICENSES, fetchPolicy: 'no-cache', variables: analyticsArgs }), { data: { analytics: { licenses: [] } } }),
          safeQuery(client.query({ query: VULNERABILITIES_HIGHLIGHTED_PRODUCTS, fetchPolicy: 'no-cache', variables: analyticsArgs }), {
            data: { analytics: { vulnerabilityHighlightedProducts: [] } }
          }),
          safeQuery(client.query({ query: CRYPTOGRAPHY_COUNT, fetchPolicy: 'no-cache', variables: analyticsArgs }), {
            data: { analytics: { cryptographyCount: {} } }
          }),
          safeQuery(client.query({ query: MISCONFIGURATION_CHECK, fetchPolicy: 'no-cache', variables: analyticsArgs }), {
            data: { analytics: { misconfigurationCheck: {} } }
          }),
          safeQuery(client.query({ query: MISCONFIGURATION_BY_CATEGORY, fetchPolicy: 'no-cache', variables: analyticsArgs }), {
            data: { analytics: { misconfigurationByCategory: [] } }
          }),
          safeQuery(client.query({ query: VULNERABILITIES, fetchPolicy: 'no-cache', variables: vulnerabilityArgs.variables }), {
            data: { vulnerabilities: { edges: [] } }
          }),
          safeQuery(client.query({ query: VULNERABILITIES, fetchPolicy: 'no-cache', variables: vulnerabilityThreatActorsArgs.variables }), {
            data: { vulnerabilities: { edges: [] } }
          }),
          safeQuery(
            client.query({
              query: DEPENDENCIES,
              fetchPolicy: 'no-cache',
              variables: dependenciesTopFiveVulnCountArgs.variables
            }),
            {
              data: { dependencies: { edges: [] } }
            }
          ),
          safeQuery(client.query({ query: GET_CREDENTIALS, fetchPolicy: 'no-cache', variables: crackedCredentialsUserArgs.variables }), {
            data: { credentials: { edges: [] } }
          }),
          safeQuery(client.query({ query: HASHES, fetchPolicy: 'no-cache', variables: crackedCredentialsUserHash.variables }), {
            data: { hashes: { edges: [] } }
          })
        ]);

        const asset = assetRes?.data?.asset;
        const vulnSummary = vulnRes?.data?.analytics?.vulnerabilitiesSummaryCount || {};
        const exploits = exploitsRes?.data?.analytics?.knownExploits || {};
        const licenses = licensesRes?.data?.analytics?.licenses || [];
        const highlightedProducts = highlightedProductsRes?.data?.analytics?.vulnerabilityHighlightedProducts || [];
        const crypto = cryptoRes?.data?.analytics?.cryptographyCount || {};
        const misconfig = misconfigRes?.data?.analytics?.misconfigurationCheck || {};
        const misconfigByCat = misconfigCatRes?.data?.analytics?.misconfigurationByCategory || [];
        const vulnsByEPSS = vulnerabilitiesByEPSS.data?.vulnerabilities?.edges;
        const vulnsThreats = vulnsThreatActorsWeaponized?.data?.vulnerabilities?.edges;
        const depTopFiveCount = dependenciesTopFiveVulnCount?.data?.dependencies?.edges;
        const crackedCredsUser = crackCredentialsUser?.data?.credentials?.edges;
        const crackedCredHash = crackedCredentialsHash?.data?.hashes?.edges;

        const extractedVulnByEPSS = vulnsByEPSS => {
          return vulnsByEPSS.map(vulnerabilityEdge => {
            const node = vulnerabilityEdge.node;
            return {
              cve: node.cve,
              epssPercentile: node.epssPercentile,
              vendor: node.vendor,
              version: node.version
            };
          });
        };

        const extractedVulnsThreatsActorWeaponized = vulns => {
          return vulns.map(vulnerabilityEdge => {
            const node = vulnerabilityEdge.node;
            return {
              cve: node.cve,
              severity: node.severity,
              vendor: node.vendor,
              version: node.version
            };
          });
        };

        const summarizeDependenciesVulnerabilities = dependenciesData => {
          return dependenciesData.map(edge => {
            const dependencyNode = edge.node;
            const dependencyInfo = dependencyNode.dependency;
            const vulnerabilityCounts = dependencyNode.analytic.vulnerability;

            // Sum all vulnerability severities
            const totalVulnerabilities =
              (vulnerabilityCounts.critical || 0) +
              (vulnerabilityCounts.high || 0) +
              (vulnerabilityCounts.medium || 0) +
              (vulnerabilityCounts.low || 0);

            return {
              name: dependencyInfo.name,
              version: dependencyInfo.version.id,
              vendor: dependencyInfo.vendor,
              vulnerabilities: {
                critical: vulnerabilityCounts.critical,
                high: vulnerabilityCounts.high,
                medium: vulnerabilityCounts.medium,
                low: vulnerabilityCounts.low,
                total: totalVulnerabilities
              }
            };
          });
        };

        function extractCredentialDetails(data) {
          return data
            .map(item => {
              const node = item.node;
              if (node) {
                return {
                  filePath: node.filePath
                };
              }
              return null;
            })
            .filter(Boolean); // Filters out any null values if 'node' was missing
        }

        // Create chart configurations
        const vulnChart = createVulnerabilitiesChart(vulnSummary);
        const exploitsChart = createKnownExploitsChart(exploits);
        const cryptoChart = createCryptographyChart(crypto);
        const misconfigChart = createMisconfigurationsChart(misconfig);
        const misconfigCatChart = createMisconfigurationsByCategoryChart(misconfigByCat);

        // Generate chart images with higher resolution
        const chartImages = {};

        if (vulnChart) {
          chartImages.vulnerabilities = await createChartImage(vulnChart, 'donut', 600, 600);
        }
        if (exploitsChart) {
          chartImages.exploits = await createChartImage(exploitsChart, 'bar', 600, 400);
        }
        if (cryptoChart) {
          chartImages.crypto = await createChartImage(cryptoChart, 'donut', 700, 800);
        }
        if (misconfigChart) {
          chartImages.misconfig = await createChartImage(misconfigChart, 'pie', 700, 600);
        }
        if (misconfigCatChart) {
          chartImages.misconfigCat = await createChartImage(misconfigCatChart, 'bar', 700, 500);
        }

        const doc = new jsPDF();
        let y = 15;
        const margin = 15;
        const sectionSpacing = 10;
        const lineHeight = 7;
        const headerColor = '#334D6E';
        const textColor = '#333333';
        const accentColor = '#FF0101';

        const pageHeight = doc.internal.pageSize.height;
        const pageWidth = doc.internal.pageSize.width;

        const checkPageBreak = neededHeight => {
          if (y + neededHeight > pageHeight - margin) {
            doc.addPage();
            y = margin;
          }
        };

        // --- Header Section ---
        const logoX = margin;
        const logoY = margin;
        const logoWidthPdf = 10;
        const logoHeightPdf = 8.75;

        if (logoDataUrl) {
          doc.addImage(logoDataUrl, 'PNG', logoX, logoY, logoWidthPdf, logoHeightPdf, undefined, 'FAST');
        }

        const textX = logoX + logoWidthPdf + 5;
        const textY = logoY + logoHeightPdf / 2 + 4;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(24);
        doc.setTextColor(headerColor);
        doc.text('Asset Report', textX, textY);
        y = textY + 10;

        doc.setDrawColor(accentColor);
        doc.setLineWidth(0.5);
        doc.line(margin, y, 210 - margin, y);
        y += sectionSpacing;

        // --- Asset Details ---
        checkPageBreak(lineHeight * 7 + sectionSpacing);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.setTextColor(headerColor);
        doc.text('Asset Details', margin, y);
        y += lineHeight;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
        doc.setTextColor(textColor);
        doc.text(`Asset Name: ${asset.name || 'N/A'}`, margin, y);
        y += lineHeight;
        doc.text(`Vendor: ${asset.vendor || 'N/A'}`, margin, y);
        y += lineHeight;
        doc.text(`Product: ${asset.product || 'N/A'}`, margin, y);
        y += lineHeight;
        doc.text(`Version: ${asset.version || 'N/A'}`, margin, y);
        y += lineHeight;
        doc.text(`Type: ${asset.type || 'N/A'}`, margin, y);
        y += lineHeight;
        doc.text(`SHA256: ${asset.sha256 || 'N/A'}`, margin, y);
        y += lineHeight;
        doc.text(`Risk Score: ${asset.risk?.score || 'N/A'}`, margin, y);
        y += lineHeight;

        y += sectionSpacing;

        // --- Top 5 Vulns by EPSS  ---
        if (vulnsByEPSS.length > 0) {
          const vulns = extractedVulnByEPSS(vulnsByEPSS);

          // Base column widths
          const cveColWidth = 60;
          const epssColWidth = 40;
          const vendorColWidth = 50;
          const versionColWidth = 60;

          // Headers
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(16);
          doc.setTextColor(headerColor);
          doc.text('Top 5 Vulnerabilities by EPSS', margin, y);
          y += lineHeight;

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(12);
          doc.setTextColor(headerColor);

          // Headers positioning based on columns widths
          doc.text('CVE', margin + 5, y);
          doc.text('EPSS %', margin + cveColWidth + 5, y);
          doc.text('Vendor', margin + cveColWidth + epssColWidth + 5, y);
          doc.text('Version', margin + cveColWidth + epssColWidth + vendorColWidth + 5, y);
          y += lineHeight;

          doc.setFont('helvetica', 'normal');
          doc.setTextColor(textColor);

          // Vulns and Epss are not that wide that's why just wrapping vendor and versions for when those reach the max width
          vulns.forEach(vuln => {
            const wrappedVendorLines = doc.splitTextToSize(`${vuln.vendor || ''}`, vendorColWidth);
            const wrappedVersionLines = doc.splitTextToSize(`${vuln.version || ''}`, versionColWidth);
            const maxLines = Math.max(wrappedVendorLines.length, wrappedVersionLines.length, 1);

            checkPageBreak(maxLines * lineHeight);

            // CVE column
            doc.text(`${vuln.cve}`, margin + 5, y);

            // Epss column
            doc.text(`${(vuln.epssPercentile * 100 || 0).toFixed(2)}%`, margin + cveColWidth + 5, y);

            // Vendor column
            wrappedVendorLines.forEach((line, index) => {
              doc.text(line, margin + cveColWidth + epssColWidth + 5, y + index * lineHeight);
            });

            // Version column
            wrappedVersionLines.forEach((line, index) => {
              doc.text(line, margin + cveColWidth + epssColWidth + vendorColWidth + 5, y + index * lineHeight);
            });

            y += maxLines * lineHeight;
          });

          y += sectionSpacing;
        }

        // --- Top 5 Threat Actors Weaponized  ---
        if (vulnsThreats.length > 0) {
          const vulns = extractedVulnsThreatsActorWeaponized(vulnsThreats);

          // Base column widths
          const cveColWidth = 60;
          const severityColWidth = 40;
          const vendorColWidth = 50;
          const versionColWidth = 60;

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(16);
          doc.setTextColor(headerColor);
          doc.text('Top 5 Threat Actors Weaponized', margin, y);
          y += lineHeight;

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(12);
          doc.setTextColor(headerColor);

          // Headers positioning based on columns widths
          doc.text('CVE', margin + 5, y);
          doc.text('Severity', margin + cveColWidth + 5, y);
          doc.text('Vendor', margin + cveColWidth + severityColWidth + 5, y);
          doc.text('Version', margin + cveColWidth + severityColWidth + vendorColWidth + 5, y);
          y += lineHeight;

          doc.setFont('helvetica', 'normal');
          doc.setTextColor(textColor);

          // CVE and Severity are not that wide that's why just wrapping vendor and versions for when those reach the max width, to avoid extra calculations
          vulns.forEach(vuln => {
            const wrappedVendorLines = doc.splitTextToSize(`${vuln.vendor || ''}`, vendorColWidth);
            const wrappedVersionLines = doc.splitTextToSize(`${vuln.version || ''}`, versionColWidth);
            const maxLines = Math.max(wrappedVendorLines.length, wrappedVersionLines.length, 1);

            checkPageBreak(maxLines * lineHeight);

            // CVE column
            doc.text(`${vuln.cve}`, margin + 5, y);

            // Severity column
            doc.text(`${vuln.severity}`, margin + cveColWidth + 5, y);

            // Vendor column
            wrappedVendorLines.forEach((line, index) => {
              doc.text(line, margin + cveColWidth + severityColWidth + 5, y + index * lineHeight);
            });

            // Version column
            wrappedVersionLines.forEach((line, index) => {
              doc.text(line, margin + cveColWidth + severityColWidth + vendorColWidth + 5, y + index * lineHeight);
            });

            y += maxLines * lineHeight;
          });

          y += sectionSpacing;
        }

        // --- Top 5 Vulnerable Components (by vuln count)  ---
        if (depTopFiveCount.length > 0) {
          const vulns = summarizeDependenciesVulnerabilities(depTopFiveCount);

          // Base column widths
          const nameColWidth = 60;
          const vendorColWidth = 40;
          const versionColWidth = 50;

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(16);
          doc.setTextColor(headerColor);
          doc.text('Top 5 Vulnerable Components (by vuln count)', margin, y);
          y += lineHeight;

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(12);
          doc.setTextColor(headerColor);

          // Headers positioning based on columns widths
          doc.text('Name', margin + 5, y);
          doc.text('Vendor', margin + nameColWidth + 5, y);
          doc.text('Version', margin + nameColWidth + vendorColWidth + 5, y);
          doc.text('Total', margin + nameColWidth + vendorColWidth + versionColWidth + 5, y);
          y += lineHeight;

          doc.setFont('helvetica', 'normal');
          doc.setTextColor(textColor);

          vulns.forEach(vuln => {
            // Wrap text for Name, Vendor, and Version
            const wrappedNameLines = doc.splitTextToSize(`${vuln?.name || ''}`, nameColWidth);
            const wrappedVendorLines = doc.splitTextToSize(`${vuln?.vendor || ''}`, vendorColWidth);
            const wrappedVersionLines = doc.splitTextToSize(`${vuln?.version || ''}`, versionColWidth);
            const maxLines = Math.max(wrappedNameLines.length, wrappedVendorLines.length, wrappedVersionLines.length, 1);

            checkPageBreak(maxLines * lineHeight);

            // Name column
            wrappedNameLines.forEach((line, index) => {
              doc.text(line, margin + 5, y + index * lineHeight);
            });

            // Vendor column
            wrappedVendorLines.forEach((line, index) => {
              doc.text(line, margin + nameColWidth + 5, y + index * lineHeight);
            });

            // Version column
            wrappedVersionLines.forEach((line, index) => {
              doc.text(line, margin + nameColWidth + vendorColWidth + 5, y + index * lineHeight);
            });

            // Total vuln, not wrapped because is not necessary since is the last one, and a short width one
            doc.text(`${vuln?.vulnerabilities?.total || 0}`, margin + nameColWidth + vendorColWidth + versionColWidth + 5, y);

            y += maxLines * lineHeight;
          });

          y += sectionSpacing;
        }

        // --- Vulnerabilities Summary with Chart ---
        const chartHeight = 80; // Height in mm for embedded chart
        checkPageBreak(lineHeight * 4 + chartHeight + sectionSpacing * 2);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.setTextColor(headerColor);
        doc.text('Vulnerabilities Summary', margin, y);
        y += lineHeight;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
        doc.setTextColor(textColor);
        doc.text(`Critical: ${vulnSummary.critical || 0}`, margin, y);
        y += lineHeight;
        doc.text(`High: ${vulnSummary.high || 0}`, margin, y);
        y += lineHeight;
        doc.text(`Medium: ${vulnSummary.medium || 0}`, margin, y);
        y += lineHeight;
        doc.text(`Low: ${vulnSummary.low || 0}`, margin, y);

        // Add vulnerabilities chart
        if (chartImages.vulnerabilities) {
          y += sectionSpacing;
          const chartWidth = 100; // Width in mm
          const chartX = (pageWidth - chartWidth) / 2; // Center the chart
          doc.addImage(chartImages.vulnerabilities, 'PNG', chartX, y, chartWidth, chartHeight, undefined, 'FAST');
          y += chartHeight;
        }

        y += sectionSpacing;

        // --- Known Exploits with Chart ---
        checkPageBreak(lineHeight * 2 + chartHeight + sectionSpacing * 2);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.setTextColor(headerColor);
        y += lineHeight;
        doc.text('Known Exploits', margin, y);
        y += lineHeight;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
        doc.setTextColor(textColor);
        doc.text(`Total Actors: ${exploits.totalActors || 0}`, margin, y);
        y += lineHeight;
        doc.text(`Total Attacks: ${exploits.totalAttacks || 0}`, margin, y);

        // Add exploits chart
        if (chartImages.exploits) {
          y += sectionSpacing;
          const chartWidth = 100;
          const chartX = (pageWidth - chartWidth) / 2;
          doc.addImage(chartImages.exploits, 'PNG', chartX, y, chartWidth, chartHeight, undefined, 'FAST');
          y += chartHeight;
        }

        y += sectionSpacing;

        // --- Cryptography with Chart ---
        checkPageBreak(lineHeight * 2 + chartHeight + sectionSpacing * 2);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.setTextColor(headerColor);
        y += lineHeight;
        doc.text('Cryptography', margin, y);
        y += lineHeight;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
        doc.setTextColor(textColor);
        doc.text(`Certificates: ${crypto.certificates || 0}`, margin, y);
        y += lineHeight;
        doc.text(`Public Keys: ${crypto.publicKeys || 0}`, margin, y);

        // Add crypto chart
        if (chartImages.crypto) {
          y += sectionSpacing;
          const chartWidth = 100;
          const chartX = (pageWidth - chartWidth) / 2;
          doc.addImage(chartImages.crypto, 'PNG', chartX, y, chartWidth, chartHeight, undefined, 'FAST');
          y += chartHeight;
        }

        y += sectionSpacing;

        // --- Misconfigurations Summary with Chart ---
        checkPageBreak(lineHeight * 3 + chartHeight + sectionSpacing * 2);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.setTextColor(headerColor);
        y += lineHeight;
        doc.text('Misconfigurations', margin, y);
        y += lineHeight;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
        doc.setTextColor(textColor);
        doc.text(`Passed: ${misconfig.passed || 0}`, margin, y);
        y += lineHeight;
        doc.text(`Failed: ${misconfig.failed || 0}`, margin, y);
        y += lineHeight;
        doc.text(`Not Applicable: ${misconfig.notApplicable || 0}`, margin, y);
        y += lineHeight;

        // Add misconfig chart
        if (chartImages.misconfig) {
          y += sectionSpacing;
          const chartWidth = 100;
          const chartX = (pageWidth - chartWidth) / 2;
          doc.addImage(chartImages.misconfig, 'PNG', chartX, y, chartWidth, chartHeight, undefined, 'FAST');
          y += chartHeight;
        }

        y += sectionSpacing;

        // --- Misconfiguration by Category with Chart ---
        if (misconfigByCat.length > 0) {
          const categoryChartHeight = 100; // Larger height for category chart
          checkPageBreak(lineHeight * (misconfigByCat.length + 2) + categoryChartHeight + sectionSpacing * 2);

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(16);
          doc.setTextColor(headerColor);
          doc.text('Misconfigurations by Category', margin, y);
          y += lineHeight;

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(12);
          doc.setTextColor(headerColor);
          doc.text('Category', margin + 5, y);
          doc.text('Passed', margin + 70, y);
          doc.text('Failed', margin + 100, y);
          doc.text('N/A', margin + 130, y);
          y += lineHeight;

          doc.setFont('helvetica', 'normal');
          doc.setTextColor(textColor);
          misconfigByCat.forEach(cat => {
            checkPageBreak(lineHeight);
            doc.text(capitalizeFirstLetter(`${cat.category}`), margin + 5, y);
            doc.text(`${cat.passed || 0}`, margin + 70, y);
            doc.text(`${cat.failed || 0}`, margin + 100, y);
            doc.text(`${cat.notApplicable || 0}`, margin + 130, y);
            y += lineHeight;
          });

          // Add category chart
          if (chartImages.misconfigCat) {
            y += sectionSpacing;
            const chartWidth = 120; // Wider for category chart
            const chartX = (pageWidth - chartWidth) / 2;
            checkPageBreak(categoryChartHeight);
            doc.addImage(chartImages.misconfigCat, 'PNG', chartX, y, chartWidth, categoryChartHeight, undefined, 'FAST');
            y += categoryChartHeight;
          }

          y += sectionSpacing;
        }

        // --- Licenses ---
        if (licenses.length > 0) {
          checkPageBreak(lineHeight * (licenses.length > 0 ? licenses.length : 1) + sectionSpacing);
          const pageWidth = doc.internal.pageSize.getWidth();
          const availableContentWidth = pageWidth - margin * 2;
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(16);
          doc.setTextColor(headerColor);
          doc.text('Licenses', margin, y);
          y += lineHeight;

          doc.setFont('helvetica', 'normal');
          doc.setFontSize(12);
          doc.setTextColor(textColor);

          licenses.forEach(lic => {
            const wrappedLicensesLines = doc.splitTextToSize(`- ${lic.license}: ${lic.count}`, availableContentWidth);
            const heightNeededForThisLicense = wrappedLicensesLines.length * lineHeight;
            checkPageBreak(heightNeededForThisLicense);
            wrappedLicensesLines.forEach((line, index) => {
              doc.text(line, margin, y + index * lineHeight);
            });
            y += heightNeededForThisLicense;
          });
        } else {
          doc.text('No license information available.', margin, y);
          y += lineHeight;
        }

        y += sectionSpacing;

        // --- Cracked Credentials  ---
        if (crackedCredsUser?.length > 0 || crackedCredHash?.length > 0) {
          checkPageBreak(lineHeight * (crackedCredsUser.length + 2) + sectionSpacing * 2);

          const credentialsUser = extractCredentialDetails(crackedCredsUser);
          const credentialsHash = extractCredentialDetails(crackedCredHash);

          const allCredentials = [...credentialsUser, ...credentialsHash];

          const pageWidth = doc.internal.pageSize.getWidth();
          const availableContentWidth = pageWidth - margin * 2;

          checkPageBreak(lineHeight * 3 + sectionSpacing);

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(16);
          doc.setTextColor(headerColor);
          doc.text('Cracked Credentials', margin, y);
          y += lineHeight;

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(12);
          doc.setTextColor(headerColor);
          doc.text('File Paths', margin, y);
          y += lineHeight;

          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.setTextColor(textColor);

          allCredentials.forEach(cred => {
            const filePathText = cred?.filePath || 'N/A';
            const wrappedFilePathLines = doc.splitTextToSize(` File Path:  ${filePathText}`, availableContentWidth);
            const heightNeededForThisCredential = wrappedFilePathLines.length * lineHeight + 5;
            checkPageBreak(heightNeededForThisCredential);
            wrappedFilePathLines.forEach((line, index) => {
              doc.text(line, margin, y + index * lineHeight);
            });
            y += heightNeededForThisCredential;
          });

          y += sectionSpacing;
        } else {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(16);
          doc.setTextColor(headerColor);
          doc.text('No Cracked Credentials found', margin, y);
          y += lineHeight;
          y += sectionSpacing;
        }

        // --- Footer ---
        doc.setFontSize(10);
        doc.setTextColor('#888888');
        doc.text(`Generated on: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`, margin, pageHeight - 10);

        // --- PAGE NUMBERS ---
        const totalPages = doc.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          doc.setPage(i);
          doc.setFontSize(10);
          doc.setTextColor('#888888');

          const pageNumberText = `Page ${i} of ${totalPages}`;
          const centerX = doc.internal.pageSize.getWidth() - 15;
          const centerY = doc.internal.pageSize.getHeight() - 10;

          doc.text(pageNumberText, centerX, centerY, { align: 'center' });
        }
        // --- END PAGE NUMBERS ---

        // Generate filename: name_with_spaces-Summary.pdf
        const assetName = asset?.name || 'asset';
        const filename = `${assetName.replace(/\s+/g, '_')}-Summary.pdf`;
        doc.save(filename);
      } catch (err) {
        enqueueSnackbar(<CustomError message={snackBarErrorMessage} />, {
          variant: 'error',
          preventDuplicate: true,
          autoHideDuration: null
        });
        console.error('PDF generation error:', err);
      } finally {
        setIsGenerating(false);
        onComplete?.();
      }
    },
    [client, enqueueSnackbar, isAiRelatedDependenciesEnabled, isComponentConfidenceEnabled, isComponentIdentifiedViaEnabled]
  );
  return { handleGeneratePDF, isGenerating };
};
